/* eslint-disable no-unsafe-optional-chaining */
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { CONTRACT, SECTION } from 'constants/global';
import PropTypes from 'prop-types';
import useLiterals from 'utils/hooks/useLiterals';
import scheduleIcon from 'assets/icons/schedule.svg';
import Button from 'components/UI/Button';
import Box from 'components/UI/Box/Box';
import { ROUTE_PATH } from 'routes';
import { formatDate } from 'utils/formatDate';
import { formatCurrency } from 'utils/formatCurrency';
import documentIcon from 'assets/icons/document.svg';
import watchIcon from 'assets/icons/watch.svg';
import ringIcon from 'assets/icons/ring.svg';
import carIcon from 'assets/icons/car.svg';
import cellPhoneIcon from 'assets/icons/cellphone.svg';
import ProgressChart from './ProgressChart';
import './styles.scss';

export const renderIconCategories = (type, isAuto = false, src = false) => {
  let imgSrc = '';
  if (isAuto) {
    imgSrc = carIcon;
  } else {
    switch (Number(type)) {
      case 1:
        imgSrc = ringIcon;
        break;
      case 2:
        imgSrc = watchIcon;
        break;
      case 3:
        imgSrc = cellPhoneIcon;
        break;
      default:
        imgSrc = scheduleIcon;
    }
  }
  return (
    src ? imgSrc : <img src={imgSrc} alt='product-icon' className='product-icon' />
  );
};

const PaymentBox = ({
  type = CONTRACT,
  data = null,
  action = null,
  showAction = true,
  inBox = true,
  actionText = '',
  actionDisabled = false,
  showCoverage = true,
}) => {
  const literals = useLiterals('contract');
  const navigate = useNavigate();
  const info = useMemo(() => {
    if (!data) {
      return null;
    }
    if (type === CONTRACT) {
      /* Auto contracts have incorrect fields, and Alexa requested this. */
      const isPeriodoAdicional = data?.DesEstatus?.toLowerCase() === 'periodo adicional';
      const rawArticleDesc = data?.Descripcion?.trim?.() || '';
      const articleDescription = rawArticleDesc.length > 50
        ? `${rawArticleDesc.substring(0, 50)}...`
        : rawArticleDesc;
      return {
        id: data?.Numero || '-',
        icon: renderIconCategories(data?.Ramo, data?.isAuto, true),
        description: `${literals.status}: ${data?.DesEstatus || '-'}`,
        articleDescription,
        paid: data.isAuto ? data?.CapitalRestante : (data?.MontoContratacion - data?.CapitalRestante) || 0,
        pending: (data.isAuto ? (data?.MontoContratacion - data?.CapitalRestante) : data?.CapitalRestante) || 0,
        capital: data?.MontoContratacion || 0,
        expirationDate: formatDate(data?.FechaPago, 'DD/MM/YYYY'),
        extemporaneousDate: isPeriodoAdicional
          ? formatDate(data?.FechaPad, 'DD/MM/YYYY')
          : null,
        commercializationDate: isPeriodoAdicional
          ? formatDate(data?.FechaCargo, 'DD/MM/YYYY')
          : null,
        isAuto: !!data?.isAuto,
        isPeriodoAdicional,
      };
    }
    if (type === SECTION) {
      return {
        id: data?.id || '-',
        icon: documentIcon,
        description: data?.description.map((desc) => desc.description).join(', ') || '-',
        paid: data?.deposits || 0,
        pending: data?.remainingCapital || 0,
        capital: data?.capital || 0,
        expirationDate: data?.expirationDate || '-',
        coverage: {
          id: data?.coverageId || '-',
          total: data?.coverageAmount || 0,
          paid: data?.coveragePaid || 0,
          pending: data?.coverageBalance || 0,
        },
      };
    }
    return null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, type]);

  const handleAction = () => {
    if (action) {
      action(info.id);
    } else if (type === CONTRACT) {
      navigate(ROUTE_PATH.setContract(info.id));
    } else {
      navigate(ROUTE_PATH.setSection(info.id));
    }
  };

  const renderHeader = () => {
    const title = type === CONTRACT ? literals.contract : literals.section;
    if (!inBox) {
      return (
        <div className='deadlines-box-header mb-4'>
          <div className='deadlines-box-header-left'>
            <h2>{title}</h2>
            <h2>{info.id}</h2>
            {info.articleDescription && (
              <p className='article-description mb-0'>{info.articleDescription}</p>
            )}
          </div>
        </div>
      );
    }
    return (
      <div className='deadlines-box-header'>
        <div className='deadlines-box-header-left'>
          <div className='deadlines-box-header-left-header mb-4'>
            <img src={info.icon} alt='product-icon' className='product-icon' />
            <div>
              <h3 className='mb-0'>
                {`${title} ${info.id}`}
              </h3>
              {info.articleDescription && (
                <p className='article-description mb-0'>{info.articleDescription}</p>
              )}
            </div>
          </div>
          {info.description && (
            <ul>
              <li>
                <h4>{info.description}</h4>
              </li>
            </ul>
          )}
        </div>
      </div>
    );
  };

  const renderProgress = (total, paid, pending, showPending = true, title = null) => {
    return (
      <div className='deadlines-box-progress mb-4'>
        {title && (
          <p className='font-semibold mb-2 mt-4'>{title}</p>
        )}
        <div className={`deadlines-box-progress-header mb-2 ${info.paid > 0 ? '' : 'without-pending'}`}>
          <div>
            <p className='text-neutral-500 mb-1'>{literals.returned}</p>
            <h4>{formatCurrency(paid)}</h4>
          </div>
          {paid > 0 && showPending ? (
            <div>
              <p className='text-neutral-500 mb-1'>{literals.pending}</p>
              <h4>{formatCurrency(pending)}</h4>
            </div>
          ) : null}
          <div>
            <p className='text-neutral-500 mb-1'>{literals.total}</p>
            <h4>{formatCurrency(total)}</h4>
          </div>
        </div>
        <div className='deadlines-box-progress-chart'>
          <ProgressChart total={total} pending={pending} />
        </div>
      </div>
    );
  };

  const renderBody = () => {
    return (
      <div className='deadlines-box mb-4'>
        {renderHeader()}
        {renderProgress(info.capital, info.paid, info.pending, !info.isAuto)}
        {info?.coverage?.total && showCoverage ? (
          renderProgress(info.coverage.total, info.coverage.paid, info.coverage.pending, true, `${literals.coverage} ${info.coverage.id}`)
        ) : null}
        <div className='deadlines-box-deadline'>
          <div className='deadlines-box-deadline-left'>
            {(type === SECTION || !info.isPeriodoAdicional) && (
              <>
                <p className='text-dark-gray deadline-label'>{literals.nextExpiration}</p>
                <p>{info.expirationDate}</p>
              </>
            )}
            {info.extemporaneousDate && (
              <>
                <p className='text-dark-gray deadline-label mt-2'>
                  {literals.extemporaneousExpiration}
                </p>
                <p>{info.extemporaneousDate}</p>
              </>
            )}
            {info.commercializationDate && (
              <>
                <p className='text-dark-gray deadline-label mt-2'>
                  {literals.commercializationStartDate}
                </p>
                <p>{info.commercializationDate}</p>
              </>
            )}
          </div>
          {showAction && (
            <div className='deadlines-box-deadline-right'>
              <Button
                size='sm'
                fullWidth
                onClick={() => handleAction()}
                text={actionText || literals.amortize}
                disabled={actionDisabled}
              />
            </div>
          )}
        </div>
      </div>
    );
  };

  if (!data) {
    return null;
  }

  if (!inBox) {
    return renderBody();
  }

  return (
    <Box className='deadlines-box mb-4' padding='sm'>
      {renderBody()}
    </Box>
  );
};

PaymentBox.displayName = 'PaymentBox';

PaymentBox.propTypes = {
  type: PropTypes.string,
  data: PropTypes.object,
  actionText: PropTypes.string,
  actionDisabled: PropTypes.bool,
  showAction: PropTypes.bool,
  inBox: PropTypes.bool,
  action: PropTypes.func,
  showCoverage: PropTypes.bool,
};

export default PaymentBox;
